import type { BrowserOptions, Event } from "@sentry/react";
import { isStringValue } from "@/utils/typePredicates";
import {
  SAFE_CONSOLE_REDACTED,
  type SafeLogValue,
  sanitizeDiagnosticString,
  sanitizeLogContext,
} from "./safeConsole";

type SentryDiagnosticValues = NonNullable<Event["extra"]>;

type SentryStreamedSpan = Parameters<
  NonNullable<BrowserOptions["beforeSendSpan"]>
>[0];

/** The fields of Sentry's streamed span payload that can carry credentials. */
type SanitizableSentrySpan = Pick<SentryStreamedSpan, "name" | "attributes">;

const REDACTION_BASE_URL = "https://sentry-redaction.invalid/";
const REDACTED_VALUE = "REDACTED";
const MAX_NESTED_URL_DEPTH = 3;
const DEFAULT_SENSITIVE_AUTH_PARAMETERS: ReadonlySet<string> = new Set([
  "oobcode",
  "invitationtoken",
]);
const BEARER_TOKEN_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const FULLY_SENSITIVE_HEADER_NAMES = new Set([
  "authorization",
  "cookie",
  "proxy-authorization",
  "set-cookie",
  "x-api-key",
  "x-auth-token",
]);
const PARAMETER_VALUE_DELIMITERS = new Set([
  "&",
  "#",
  " ",
  "\t",
  "\n",
  "\r",
  '"',
  "'",
]);

interface FirebaseActionUrlRedactionOptions {
  /**
   * Lowercase parameter names to redact. Sentry defaults to `oobCode`;
   * callers can opt into additional Firebase parameters such as `apiKey`.
   */
  sensitiveParameterNames?: ReadonlySet<string>;
  redactedValue?: string;
}

const isSensitiveAuthParameter = (
  name: string,
  sensitiveParameterNames: ReadonlySet<string>,
): boolean => sensitiveParameterNames.has(name.toLowerCase());

interface SensitiveParameterMatch {
  parameterStart: number;
  prefixLength: number;
}

const findNextSensitiveParameter = (
  lowerValue: string,
  searchFrom: number,
  sensitiveParameterNames: ReadonlySet<string>,
): SensitiveParameterMatch | null => {
  let nextMatch: SensitiveParameterMatch | null = null;

  for (const name of sensitiveParameterNames) {
    if (!name) continue;
    const prefix = `${name.toLowerCase()}=`;
    let parameterStart = lowerValue.indexOf(prefix, searchFrom);
    while (parameterStart !== -1) {
      const precedingCharacter = lowerValue[parameterStart - 1];
      if (
        parameterStart === 0 ||
        precedingCharacter === "?" ||
        precedingCharacter === "#" ||
        precedingCharacter === "&"
      ) {
        break;
      }
      parameterStart = lowerValue.indexOf(prefix, parameterStart + 1);
    }

    if (
      parameterStart !== -1 &&
      (!nextMatch || parameterStart < nextMatch.parameterStart)
    ) {
      nextMatch = { parameterStart, prefixLength: prefix.length };
    }
  }

  return nextMatch;
};

const redactDirectParameter = (
  value: string,
  sensitiveParameterNames: ReadonlySet<string>,
  redactedValue: string,
): string => {
  const lowerValue = value.toLowerCase();
  let searchFrom = 0;
  let result = "";

  while (searchFrom < value.length) {
    const match = findNextSensitiveParameter(
      lowerValue,
      searchFrom,
      sensitiveParameterNames,
    );
    if (!match) {
      result += value.slice(searchFrom);
      break;
    }

    const valueStart = match.parameterStart + match.prefixLength;
    let valueEnd = valueStart;
    while (
      valueEnd < value.length &&
      !PARAMETER_VALUE_DELIMITERS.has(value[valueEnd])
    ) {
      valueEnd += 1;
    }

    result += `${value.slice(searchFrom, valueStart)}${redactedValue}`;
    searchFrom = valueEnd;
  }

  return result;
};

const rebuildUrl = (original: string, sanitized: URL): string => {
  const path = `${sanitized.pathname}${sanitized.search}${sanitized.hash}`;
  if (sanitized.origin !== new URL(REDACTION_BASE_URL).origin) {
    return sanitized.toString();
  }
  if (original.startsWith("//")) return `//${sanitized.host}${path}`;
  if (original.startsWith("/")) return path;
  if (original.startsWith("?")) {
    return `${sanitized.search}${sanitized.hash}`;
  }
  return `${sanitized.pathname.slice(1)}${sanitized.search}${sanitized.hash}`;
};

/**
 * Redact Firebase email-action credentials from a URL while retaining enough
 * route context for diagnostics. Nested URL query values are covered as well.
 */
const redactFirebaseActionUrlAtDepth = (
  value: string,
  depth: number,
  sensitiveParameterNames: ReadonlySet<string>,
  redactedValue: string,
): string => {
  if (depth > MAX_NESTED_URL_DEPTH) return value;
  const directlyRedacted = redactDirectParameter(
    value,
    sensitiveParameterNames,
    redactedValue,
  );

  let parsed: URL;
  try {
    parsed = new URL(directlyRedacted, REDACTION_BASE_URL);
  } catch {
    return directlyRedacted;
  }

  const entries = [...parsed.searchParams.entries()];
  let parsedQueryChanged = false;
  const sanitizedEntries = entries.map(([name, entryValue]) => {
    if (isSensitiveAuthParameter(name, sensitiveParameterNames)) {
      if (entryValue !== redactedValue) parsedQueryChanged = true;
      return [name, redactedValue] as const;
    }

    const sanitizedValue = redactFirebaseActionUrlAtDepth(
      entryValue,
      depth + 1,
      sensitiveParameterNames,
      redactedValue,
    );
    if (sanitizedValue !== entryValue) parsedQueryChanged = true;
    return [name, sanitizedValue] as const;
  });

  if (!parsedQueryChanged) return directlyRedacted;

  parsed.search = "";
  for (const [name, entryValue] of sanitizedEntries) {
    parsed.searchParams.append(name, entryValue);
  }
  return rebuildUrl(directlyRedacted, parsed);
};

export const redactFirebaseActionUrl = (
  value: string,
  options: FirebaseActionUrlRedactionOptions = {},
): string =>
  redactFirebaseActionUrlAtDepth(
    value,
    0,
    options.sensitiveParameterNames ?? DEFAULT_SENSITIVE_AUTH_PARAMETERS,
    options.redactedValue ?? REDACTED_VALUE,
  );

const redactStringValues = (
  values: SentryDiagnosticValues,
): SentryDiagnosticValues =>
  Object.fromEntries(
    Object.entries(values).map(([name, value]) => [
      name,
      isStringValue(value) ? redactFirebaseActionUrl(value) : value,
    ]),
  );

const redactStringRecord = (
  values: Record<string, string>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(values).map(([name, value]) => [
      name,
      redactFirebaseActionUrl(value),
    ]),
  );

const redactSensitiveHeaderValue = (value: string): string =>
  redactFirebaseActionUrl(value)
    .replace(BEARER_TOKEN_PATTERN, `Bearer ${REDACTED_VALUE}`)
    .replace(JWT_PATTERN, REDACTED_VALUE);

const redactHeaders = (
  headers: Record<string, string>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(headers).map(([name, value]) => [
      name,
      FULLY_SENSITIVE_HEADER_NAMES.has(name.toLowerCase())
        ? REDACTED_VALUE
        : redactSensitiveHeaderValue(value),
    ]),
  );

const isSafeLogRecord = (
  value: SafeLogValue,
): value is Record<string, SafeLogValue> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Final transport-boundary defense for every streamed span, including the
 * segment span that carries the page URL. Span attributes are recursively
 * converted to bounded, credential-safe diagnostic values before Sentry builds
 * the envelope.
 */
export const sanitizeSentrySpan = <T extends SanitizableSentrySpan>(
  span: T,
): T => {
  span.name = sanitizeDiagnosticString(span.name);
  const sanitizedAttributes = sanitizeLogContext(span.attributes).data;
  span.attributes = isSafeLogRecord(sanitizedAttributes)
    ? sanitizedAttributes
    : { sanitization: SAFE_CONSOLE_REDACTED };
  return span;
};

/**
 * Final transport-boundary defense for error events. The
 * login flow retains the action code only in memory until Firebase consumes it;
 * Sentry never needs the credential in requests, breadcrumbs, or trace spans.
 */
export const redactFirebaseActionCodeFromSentryEvent = <T extends Event>(
  event: T,
): T => {
  if (event.request) {
    if (event.request.url) {
      event.request.url = redactFirebaseActionUrl(event.request.url);
    }
    // Request bodies and cookies are never required for diagnostics here and
    // may contain passwords, Firebase ID tokens, or authenticated sessions.
    delete event.request.data;
    if (event.request.headers) {
      event.request.headers = redactHeaders(event.request.headers);
    }
    if (event.request.env) {
      event.request.env = redactStringRecord(event.request.env);
    }
    delete event.request.cookies;

    const query = event.request.query_string;
    if (isStringValue(query)) {
      const hasQuestionMark = query.startsWith("?");
      const sanitized = redactFirebaseActionUrl(
        hasQuestionMark ? query : `?${query}`,
      );
      event.request.query_string = hasQuestionMark
        ? sanitized
        : sanitized.slice(1);
    } else if (Array.isArray(query)) {
      event.request.query_string = query.map(
        ([name, value]): [string, string] => [
          name,
          isSensitiveAuthParameter(name, DEFAULT_SENSITIVE_AUTH_PARAMETERS)
            ? REDACTED_VALUE
            : redactFirebaseActionUrl(value),
        ],
      );
    } else if (query) {
      event.request.query_string = Object.fromEntries(
        Object.entries(query).map(([name, value]) => [
          name,
          isSensitiveAuthParameter(name, DEFAULT_SENSITIVE_AUTH_PARAMETERS)
            ? REDACTED_VALUE
            : redactFirebaseActionUrl(value),
        ]),
      );
    }
  }

  if (event.message) {
    event.message = redactFirebaseActionUrl(event.message);
  }
  if (event.transaction) {
    event.transaction = redactFirebaseActionUrl(event.transaction);
  }
  if (event.logentry?.message) {
    event.logentry.message = redactFirebaseActionUrl(event.logentry.message);
  }
  if (event.logentry?.params) {
    event.logentry.params = event.logentry.params.map((parameter) =>
      isStringValue(parameter)
        ? redactFirebaseActionUrl(parameter)
        : sanitizeLogContext(parameter).data,
    );
  }

  for (const exception of event.exception?.values ?? []) {
    if (exception.value) {
      exception.value = redactFirebaseActionUrl(exception.value);
    }
  }

  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (breadcrumb.message) {
      breadcrumb.message = redactFirebaseActionUrl(breadcrumb.message);
    }
    if (breadcrumb.data) {
      breadcrumb.data = redactStringValues(breadcrumb.data);
    }
  }

  if (event.contexts?.trace?.data) {
    event.contexts.trace.data = redactStringValues(event.contexts.trace.data);
  }

  if (event.extra) {
    event.extra = redactStringValues(event.extra);
  }

  return event;
};
